/* eslint-disable react-hooks/exhaustive-deps */
import parse from "html-react-parser";
import React, { useContext, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import { AppRulesContext } from "../../App";
import PlansApi from "../../api/PlansApi";
import { PricingPlanCard } from "../../components/cards";
import { CardLoader } from "../../components/content-loader";
import { NothingAvailable } from "../../components/placeholders";
import { EToastvariant, IPlansRoot, IPricingPageSettingsForm } from "../../config/interfaces";
import { useAppDispatch, useAppSelector } from "../../redux/Store";
import {
  ITRFilingSelector,
  getFilingDetails,
  setCheckOutPlan
} from "../../redux/slice/ITRFilingSlice";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../services";

type Props = {};

const PricingPlans: React.FC<Props> = (props: Props) => {
  const { filingUUID, planType } = useParams();
  const navigate = useNavigate();
  const { rulesSvc } = useContext(AppRulesContext);

  const dispatch = useAppDispatch();
  const { currentFilingPlan, PlansLoadingStatus } = useAppSelector(ITRFilingSelector);

  const [pricingPlans, setPricingPlans] = useState<IPlansRoot[]>([]);
  const [PricingPageSettingData, setPricingPageSettingData] = useState<IPricingPageSettingsForm>();

  useEffect(() => {
    getPricingPlans();
    getPricingPageSettingDetails();
  }, [currentFilingPlan]);

  useEffect(() => {
    if (filingUUID) {
      dispatch(getFilingDetails(filingUUID));
    }
  }, [filingUUID]);

  const getPricingPageSettingDetails = async () => {
    try {
      const _values = {
        keys: ["PRICING_PAGE_TITLE", "PRICING_PAGE_SUB_TITLE", "PRICING_PAGE_FOOTER"]
      };
      const plansApi = PlansApi.getInstance();
      const response = await plansApi.getPricingPageSettingsByKey(_values);
      const _data = response?.data?.records;
      const _pricingPageSettingData = {
        pricingTitle: _data.find((q: any) => q.key === "PRICING_PAGE_TITLE").value || "",
        pricingTitleUuId: _data.find((q: any) => q.key === "PRICING_PAGE_TITLE").uuid || "",
        pricingSubTitle: _data.find((q: any) => q.key === "PRICING_PAGE_SUB_TITLE").value || "",
        pricingSubTitleUuId: _data.find((q: any) => q.key === "PRICING_PAGE_SUB_TITLE").uuid || "",
        footerContent: _data.find((q: any) => q.key === "PRICING_PAGE_FOOTER").value || "",
        footerContentUuId: _data.find((q: any) => q.key === "PRICING_PAGE_FOOTER").uuid || ""
      };
      setPricingPageSettingData(() => _pricingPageSettingData);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    }
  };

  const getPricingPlans = async () => {
    try {
      const plansApi = PlansApi.getInstance();
      const { records } = await plansApi.getPlans({
        offset: 0,
        count: -1,
        orderBy: "createdAt",
        order: "ASC"
      });

      const sortedRecords = records.sort((p1: any, p2: any) => {
        if (Number(p1.pricingTitle) < Number(p2.pricingTitle)) return -1;
        if (Number(p1.pricingTitle) > Number(p2.pricingTitle)) return 1;
        return 0;
      });

      const currentPlanPrice =
        sortedRecords.find((plan) => plan.uuid === currentFilingPlan?.planData?.uuid)
          ?.pricingTitle || 0;

      const filteredPlans = sortedRecords.filter((plan) => {
        if (plan.type === planType) {
          if (Number(plan.pricingTitle) <= Number(currentPlanPrice)) {
            plan.isDisabledPlan = true;
          } else {
            plan.isDisabledPlan = false;
          }
          return plan;
        }
        return null;
      });

      setPricingPlans(() => filteredPlans);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    }
  };

  return (
		<Container className="w-1170">
			{PlansLoadingStatus === "PENDING" ? (
				<CardLoader cardType="details" />
			) : PlansLoadingStatus === "REJECTED" ? (
				<div className="text-center my-5">
					<NothingAvailable
						title="Something Went Wrong"
						message="Failed to fetch data from the server. Please try again later."
						btnTitle="Go to Filing"
						onBtnPress={() => navigate("/itr-filings", { replace: true })}
						btnRules={rulesSvc.userPlanList}
					/>
				</div>
			) : (
				<>
					<div className="mb-5">
						<h2 className="fw-bold">
							{PricingPageSettingData?.pricingTitle || "Pricing for all types of filings"}
						</h2>
						{PricingPageSettingData?.pricingSubTitle && (
							<p className="leading-para text-greyDark">
								{PricingPageSettingData?.pricingSubTitle}
							</p>
						)}
					</div>

					<PricingPlanCard
						pricingPlans={pricingPlans}
						handlePlanSelect={(plan) => {
							dispatch(setCheckOutPlan(plan));
							navigate(`/itr-filings/${filingUUID}/assisted/plan-checkout`);
						}}
					/>

					{PricingPageSettingData?.footerContent && (
						<div className="plan-page-footer">
							{parse(String(PricingPageSettingData?.footerContent))}
						</div>
					)}
				</>
			)}
		</Container>
	);
};

export default PricingPlans;
